import { Modal, useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Button, TextArea } from '@sgcmpteltd/uikit-react';
import {
  BUTTON_COLOR_RED,
  BUTTON_VARIANT_CONTAINED,
} from '@sgcmpteltd/uikit-react/components/button';
import cx from 'clsx';
import { useState } from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { sendMagicLinkAction } from '@/actions/member/send-magic-link';
import { postEnquiryFormStep2 } from '@/actions/new-car/enquiry-form/post';
import {
  ErrorStep,
} from '@/app/(routes)/listing/_components/page-content/alert-modal/unknown-error'; // @suppress longLineCheck

import { SuccessStep } from '../success';
import {
  BACKDROP_TRANSITION_DURATION_MS_DEFAULT,
  ENQUIRY_FORM_SUGGESTIVE_DOWNPAYMENT_TEXT,
  ENQUIRY_FORM_SUGGESTIVE_INSTALMENT_TEXT,
  ENQUIRY_FORM_SUGGESTIVE_LOAN_TEXT,
  ENQUIRY_FORM_SUGGESTIVE_STOCK_TEXT,
  ENQUIRY_FORM_SUGGESTIVE_TRADE_IN_TEXT,
  MODAL_STEP_START,
  MODAL_STEP_SUCCESS,
  MODAL_STEP_UNKNOWN_ERROR,
  MODAL_SUCCESS_MESSAGE,
  REG_SOURCE_NC_ENQUIRY,
  REG_SOURCE_NC_ENQUIRY_SPOTLIGHT,
} from './constants';
import styles from './styles.module.scss';
import type { Component, Step } from './types';

export const EnquiryFormModal: Component = ({
  enquiryFormId,
  dealersCount,
  isFromMobile,
  submodelId,
  isOpen,
  userEmail,
  userMobile,
  backdropTransitionDurationMs = BACKDROP_TRANSITION_DURATION_MS_DEFAULT,
  onClose,
  isLoggedIn,
  isSpotlight,
}) => {
  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();
  const [preference, setPreference] = useState<string>('');
  const [step, setStep] = useState<Step>(MODAL_STEP_START);
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleCloseModal = () => {
    setStep('start');
    onClose?.();
  };

  const handleClick = (text: string) => {
    setPreference(`${preference} ${text}`);
  };

  const handleQuickSignUp = async () => {
    if (!userEmail || !userMobile) return;

    try {
      const formData = new FormData();

      formData.append('email', userEmail);
      formData.append('mobileNumber', userMobile);

      const result = await sendMagicLinkAction(formData);

      if (result.status === ACTION_STATUS_SUCCESS && result.data?.email) {
        setConfirmedEmail(result.data.email);
      } else {
        throw new Error('Failed to send magic link');
      }
    } catch (error) {
      throw new Error('Failed to send magic link');
    }
  };

  const handlePostEnquiryFormStep2 = async () => {
    const { ga, gtag, dataLayer } = window;

    if (!ga) {
      return;
    }

    const eventCategory = isDesktopMode ? 'Landing Page' : 'Mobile PIsms2';
    const eventAction = 'click';
    const eventLabel = isDesktopMode ? 'PIsms2' : 'Landing Page';
    const eventName = isDesktopMode ? 'PIsms2' : 'Mobile PIsms2';

    ga('send', 'event', eventCategory, eventAction, eventName);

    if (isDesktopMode) {
      if (gtag) {
        gtag('event', eventName, {
          eventCategory,
          eventAction,
          eventLabel: eventName,
        });
      }
    } else if (dataLayer) {
      dataLayer.push({ eventDetail: null });
      dataLayer.push({
        event: eventName,
        eventDetail: {
          eventAction,
          eventCategory,
          eventLabel,
        },
      });
    }
    const formData = new FormData();

    formData.append('msg', preference);
    formData.append('enquiryId', `${enquiryFormId}`);
    formData.append('dealerCount', `${dealersCount}`);
    formData.append('isFromMobile', `${isFromMobile}`);

    if (submodelId) {
      formData.append('submodelId', `${submodelId}`);
    }

    const actionResult = await postEnquiryFormStep2(
      formData,
    );

    if (actionResult.status !== ACTION_STATUS_SUCCESS) {
      throw new Error('Failed to post enquiry form');
    }
  };

  const handleEnquiryFormSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (!isLoggedIn) {
        await handleQuickSignUp();
      }
      await handlePostEnquiryFormStep2();
      setStep(MODAL_STEP_SUCCESS);
    } catch (error) {
      console.error(error);
      setStep(MODAL_STEP_UNKNOWN_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (step === MODAL_STEP_START) {
      return (
        <>
          <TextArea
            className={styles.textArea}
            placeholder="Enter here..."
            rows={10}
            value={preference}
            onChange={({
              target: {
                value: newText,
              },
            }) => {
              setPreference(newText);
            }} />
          <div className={styles.suggestiveText}>Suggestive text</div>
          <div className={styles.pillButtonGroup}>
            <button
              className={styles.pillButton}
              // eslint-disable-next-line @stylistic/max-len
              onClick={() => handleClick(ENQUIRY_FORM_SUGGESTIVE_TRADE_IN_TEXT)}>
              {ENQUIRY_FORM_SUGGESTIVE_TRADE_IN_TEXT}
            </button>
            <button
              className={styles.pillButton}
              // eslint-disable-next-line @stylistic/max-len
              onClick={() => handleClick(ENQUIRY_FORM_SUGGESTIVE_DOWNPAYMENT_TEXT)}>
              {ENQUIRY_FORM_SUGGESTIVE_DOWNPAYMENT_TEXT}
            </button>
            <button
              className={styles.pillButton}
              onClick={() => handleClick(ENQUIRY_FORM_SUGGESTIVE_LOAN_TEXT)}>
              {ENQUIRY_FORM_SUGGESTIVE_LOAN_TEXT}
            </button>
            <button
              className={styles.pillButton}
              // eslint-disable-next-line @stylistic/max-len
              onClick={() => handleClick(ENQUIRY_FORM_SUGGESTIVE_INSTALMENT_TEXT)}>
              {ENQUIRY_FORM_SUGGESTIVE_INSTALMENT_TEXT}
            </button>
            <button
              className={styles.pillButton}
              onClick={() => handleClick(ENQUIRY_FORM_SUGGESTIVE_STOCK_TEXT)}>
              {ENQUIRY_FORM_SUGGESTIVE_STOCK_TEXT}
            </button>
          </div>
          <div className={styles.containerButton}>
            <Button
              className={styles.buttonSubmit}
              color={BUTTON_COLOR_RED}
              disabled={isSubmitting}
              variant={BUTTON_VARIANT_CONTAINED}
              onClick={() => handleEnquiryFormSubmit()}>
              Submit
            </Button>
          </div>
        </>
      );
    }

    if (step === MODAL_STEP_SUCCESS) {
      return (
        <SuccessStep
          className={styles.alignToTop}
          regSource={isSpotlight
            ? REG_SOURCE_NC_ENQUIRY_SPOTLIGHT
            : REG_SOURCE_NC_ENQUIRY}
          successMessage={MODAL_SUCCESS_MESSAGE}
          userEmail={confirmedEmail}
          userMobile={userMobile} />
      );
    }

    if (step === MODAL_STEP_UNKNOWN_ERROR) {
      return <ErrorStep className={styles.alignToTop} />;
    }

    return null;
  };

  return (
    <Modal
      backdropTransitionDurationMs={backdropTransitionDurationMs}
      className={styles.containerRoot}
      contentClassName={cx(
        styles.containerModalContent,
        styles.containerEnquiryFormModal,
      )}
      headerClassName={cx(
        styles.containerHeader,
      )}
      isOpen={isOpen}
      title={step === 'start' ? 'Tell us more about your preferences' : ''}
      onClose={handleCloseModal}>
      {renderContent()}
    </Modal>
  );
};
